/**
 * 导演项目卡片（参考 EcommerceReplicate 视觉语言）
 *
 * - 大圆角白卡 + 浅阴影 + 浅蓝徽标
 * - 封面：第 1 个分镜的锁定 keyframe，否则深色 hero 渐变占位
 * - 状态徽标：对应 6 种状态色
 * - 右上角菜单：复制 / 归档（或恢复）/ 彻底删除
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Clapperboard,
  MoreHorizontal,
  Copy,
  Archive,
  ArchiveRestore,
  Trash2,
  Film,
  Pencil,
  Share2,
} from 'lucide-react';
import type {
  DirectorProjectStatus,
  DirectorProjectWithCover,
} from '../services/directorApi';
import { getProjectStatusBadgeClass } from './styleTokens';
import { formatBeijingDate } from '../../../utils/time';
import { resolveBrowserMediaUrl } from '../../../utils/storageUrl';

interface ProjectCardProps {
  project: DirectorProjectWithCover;
  onEdit?: (id: string) => void;
  onDuplicate?: (id: string) => void;
  onShare?: (id: string) => void;
  onArchive?: (id: string) => void;
  onUnarchive?: (id: string) => void;
  onDelete?: (id: string) => void;
}

const STATUS_LABEL: Record<DirectorProjectStatus, string> = {
  draft: '草稿',
  producing: '制作中',
  ready: '待合成',
  composing: '合成中',
  completed: '已完成',
  archived: '已归档',
};

export function ProjectCard({
  project,
  onEdit,
  onDuplicate,
  onShare,
  onArchive,
  onUnarchive,
  onDelete,
}: ProjectCardProps) {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const statusLabel = STATUS_LABEL[project.status] ?? STATUS_LABEL.draft;
  const statusClass = getProjectStatusBadgeClass(project.status);

  const handleClick = () => {
    if (!menuOpen) navigate(`/apps/director/${project.id}`);
  };

  return (
    <div
      onClick={handleClick}
      className="group relative cursor-pointer overflow-hidden rounded-3xl border border-white/70 bg-white/85 shadow-[0_18px_50px_rgba(15,23,42,0.06)] backdrop-blur-xl transition hover:-translate-y-0.5 hover:shadow-[0_24px_70px_rgba(15,23,42,0.1)]"
    >
      {/* 封面 */}
      <div className="relative aspect-video overflow-hidden">
        {project.coverUrl ? (
          <img
            src={resolveBrowserMediaUrl(project.coverUrl)}
            alt={project.name}
            className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.03]"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800">
            <div className="absolute right-[-40px] top-[-40px] h-32 w-32 rounded-full bg-sky-400/20 blur-2xl" />
            <Clapperboard className="h-14 w-14 text-sky-200/80" />
          </div>
        )}
        {project.coverUrl && (
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950/55 via-transparent to-transparent opacity-0 transition group-hover:opacity-100" />
        )}
        {/* 状态徽标浮在封面右上 */}
        <span className={`absolute left-3 top-3 ${statusClass} shadow-sm`}>{statusLabel}</span>

        {/* 菜单按钮 */}
        <div className="absolute right-3 top-3" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            onClick={() => setMenuOpen(!menuOpen)}
            className="rounded-full bg-white/90 p-1.5 text-slate-700 shadow-sm transition hover:bg-white"
            aria-label="项目菜单"
          >
            <MoreHorizontal className="h-4 w-4" />
          </button>
          {menuOpen && (
            <div className="absolute right-0 top-9 z-10 min-w-[140px] overflow-hidden rounded-2xl border border-slate-100 bg-white py-1 shadow-lg shadow-slate-200/80">
              {onEdit && (
                <button
                  type="button"
                  onClick={() => {
                    setMenuOpen(false);
                    onEdit(project.id);
                  }}
                  className="flex w-full items-center gap-2 px-3 py-2 text-sm text-slate-700 transition hover:bg-sky-50 hover:text-sky-700"
                >
                  <Pencil className="h-4 w-4" /> 编辑
                </button>
              )}
              {onDuplicate && (
                <button
                  type="button"
                  onClick={() => {
                    setMenuOpen(false);
                    onDuplicate(project.id);
                  }}
                  className="flex w-full items-center gap-2 px-3 py-2 text-sm text-slate-700 transition hover:bg-sky-50 hover:text-sky-700"
                >
                  <Copy className="h-4 w-4" /> 复制
                </button>
              )}
              {onShare && project.lastCompositionId && (
                <button
                  type="button"
                  onClick={() => {
                    setMenuOpen(false);
                    onShare(project.id);
                  }}
                  className="flex w-full items-center gap-2 px-3 py-2 text-sm text-slate-700 transition hover:bg-sky-50 hover:text-sky-700"
                >
                  <Share2 className="h-4 w-4" /> 分享
                </button>
              )}
              {project.isArchived
                ? onUnarchive && (
                    <button
                      type="button"
                      onClick={() => {
                        setMenuOpen(false);
                        onUnarchive(project.id);
                      }}
                      className="flex w-full items-center gap-2 px-3 py-2 text-sm text-slate-700 transition hover:bg-sky-50 hover:text-sky-700"
                    >
                      <ArchiveRestore className="h-4 w-4" /> 恢复
                    </button>
                  )
                : onArchive && (
                    <button
                      type="button"
                      onClick={() => {
                        setMenuOpen(false);
                        onArchive(project.id);
                      }}
                      className="flex w-full items-center gap-2 px-3 py-2 text-sm text-slate-700 transition hover:bg-sky-50 hover:text-sky-700"
                    >
                      <Archive className="h-4 w-4" /> 归档
                    </button>
                  )}
              {onDelete && (
                <button
                  type="button"
                  onClick={() => {
                    setMenuOpen(false);
                    onDelete(project.id);
                  }}
                  className="flex w-full items-center gap-2 px-3 py-2 text-sm text-rose-600 transition hover:bg-rose-50"
                >
                  <Trash2 className="h-4 w-4" /> 彻底删除
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* 内容 */}
      <div className="space-y-2 p-4">
        <h3 className="line-clamp-1 text-base font-semibold text-slate-900">
          {project.name}
        </h3>

        {project.description && (
          <p className="line-clamp-2 text-xs leading-5 text-slate-500">{project.description}</p>
        )}

        {/* 项目配置徽标：比例 / 清晰度 / 视频模型 */}
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-600">
            {project.aspectRatio}
          </span>
          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-600">
            {project.resolution}
          </span>
          {project.videoModelName && (
            <span
              className="max-w-[140px] truncate rounded-full bg-sky-50 px-2 py-0.5 text-[10px] font-medium text-sky-700"
              title={project.videoModelName}
            >
              {project.videoModelName}
            </span>
          )}
        </div>

        <div className="flex items-center justify-between text-xs text-slate-500">
          <span className="inline-flex items-center gap-1">
            <Film className="h-3.5 w-3.5 text-sky-500" /> {project.shotCount} 个分镜
          </span>
          <span>{formatBeijingDate(project.createdAt)}</span>
        </div>
      </div>
    </div>
  );
}
